import {
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/Button/Button';
import DateTimePicker from 'components/DateTimePicker/DateTimePicker';
import TextInput from 'components/TextInput/TextInput';
import CheckIcon from 'mdi-material-ui/Check';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { closeAddNewUserDialog } from 'store/projectUsers/actions/project-users.actions';
import { selectAddNewUserDialogMeta } from 'store/projectUsers/selectors/projectUsers.selectors';
import { createOrUpdateUser } from 'store/user/thunks/createOrUpdateUser.thunk';
import { joinExistingEmployeeToProject } from 'store/resources/thunks/joinExistingEmployeeToProject.thunk';
import { TAX_NUMBER_REGEX, checkTaxNumberWithVero } from './avatarIdentity';

// react-phone-input-2 (countryCodeEditable={false}) leaves the bare dial code
// in the field once it has been touched, so dial-code-only counts as empty.
const isPhoneEffectivelyEmpty = (value, dialCode) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits === '' || digits === String(dialCode || '').replace(/\D/g, '');
};

export const AddUserDialog = () => {
  const useStyles = makeStyles((theme) => ({
    dialogPaper: {
      ...theme.component.dialogPaper,
      [theme.breakpoints.down('xs')]: {
        ...theme.component.dialogPaperFullscreen,
      },
    },
    dialogContent: {
      paddingTop: theme.spacing(2),
    },
    dialogActions: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
    buttonProgress: {
      marginRight: theme.spacing(1),
    },
  }));

  const dispatch = useDispatch();

  const classes = useStyles();

  const { t } = useTranslation('common');

  const selectedProject = useSelector(selectSelectedProject);
  const dialogMeta = useSelector(selectAddNewUserDialogMeta);
  const shouldShowRecordDate = Boolean(dialogMeta?.createRecordForUser);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('358');
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [taxNumber, setTaxNumber] = useState('');
  const [recordDate, setRecordDate] = useState(moment());

  // 'idle' | 'checking' | 'valid' | 'invalid' | 'error'
  const [taxCheckStatus, setTaxCheckStatus] = useState('idle');
  const [taxNumberBlurred, setTaxNumberBlurred] = useState(false);
  // `${taxNumber}|${dateOfBirth}` of the last completed Vero check, so an
  // unchanged pair is not re-checked.
  const checkedTaxAndDobRef = useRef('');
  const activeTaxCheckRef = useRef('');

  const [suggestedUser, setSuggestedUser] = useState(null);
  const [joining, setJoining] = useState(false);
  // The first "Lisää käyttäjä" request (Vero + lookups) can take seconds.
  const [submitting, setSubmitting] = useState(false);

  const [projectKey, setProjectKey] = useState(
    selectedProject?.projectKey || ''
  );
  const [businessId, setBusinessId] = useState(dialogMeta?.businessId || '');
  const [employerError, setEmployerError] = useState(false);
  const projectId = dialogMeta?.projectId || selectedProject?.id;

  // Only the record dialog (aikaleimat → Uusi merkintä) opens this dialog with
  // it: there a user from another company may be added when every identity
  // detail typed matches. The org chart and orientation form behave as before.
  const allowJoinByIdentity = Boolean(dialogMeta?.allowJoinByIdentity);

  // A suggestion was checked against the details typed so far.
  const clearIdentitySuggestion = () => {
    if (allowJoinByIdentity) {
      setSuggestedUser(null);
    }
  };

  // Without a phone number the backend only creates the user if Vero confirms
  // the tax number, and the Vero check needs the date of birth too.
  const phoneProvided = !isPhoneEffectivelyEmpty(phoneNumber, dialCode);
  const trimmedTaxNumber = taxNumber.trim();
  const dateOfBirthString = dateOfBirth
    ? moment(dateOfBirth).format('YYYY-MM-DD')
    : '';
  const taxPathComplete =
    TAX_NUMBER_REGEX.test(trimmedTaxNumber) && Boolean(dateOfBirthString);
  const taxPathReady = taxPathComplete && taxCheckStatus === 'valid';

  // Format feedback only matters on the phone-less path (with a phone the
  // backend stores the tax number as-is, unvalidated) and only after the
  // field has been visited, so the hint doesn't nag mid-typing.
  const taxNumberFormatInvalid =
    !phoneProvided &&
    taxNumberBlurred &&
    trimmedTaxNumber.length > 0 &&
    !TAX_NUMBER_REGEX.test(trimmedTaxNumber);

  const taxNumberHelperText =
    taxCheckStatus === 'invalid'
      ? t('add-user-dialog.tax-number-invalid')
      : taxCheckStatus === 'error'
        ? t('add-user-dialog.tax-number-check-failed')
        : taxNumberFormatInvalid && trimmedTaxNumber.length < 12
          ? t('profile.tax_number_too_short')
          : taxNumberFormatInvalid
            ? t('add-user-dialog.tax-number-invalid')
            : undefined;

  const resetTaxCheck = () => {
    setTaxCheckStatus('idle');
    checkedTaxAndDobRef.current = '';
    activeTaxCheckRef.current = '';
  };

  const runTaxCheck = async () => {
    if (phoneProvided || !taxPathComplete) {
      return;
    }
    const key = `${trimmedTaxNumber}|${dateOfBirthString}`;
    // MUI wires the field's onBlur twice (component prop + inputProps), so a
    // single blur invokes this handler twice within one render closure — the
    // ref, not state, must carry the in-flight dedupe.
    if (activeTaxCheckRef.current === key) {
      return;
    }
    if (checkedTaxAndDobRef.current === key && taxCheckStatus !== 'error') {
      return;
    }
    activeTaxCheckRef.current = key;
    setTaxCheckStatus('checking');
    try {
      const valid = await checkTaxNumberWithVero(
        trimmedTaxNumber,
        dateOfBirthString
      );
      if (activeTaxCheckRef.current !== key) {
        // Input changed while the request was in flight.
        return;
      }
      checkedTaxAndDobRef.current = key;
      setTaxCheckStatus(valid ? 'valid' : 'invalid');
    } catch (error) {
      console.error('Tax number check failed:', error);
      if (activeTaxCheckRef.current === key) {
        // Network/Vero failure — distinct from an invalid number so the user
        // can retry or fall back to entering a phone number.
        setTaxCheckStatus('error');
      }
    } finally {
      if (activeTaxCheckRef.current === key) {
        activeTaxCheckRef.current = '';
      }
    }
  };

  useEffect(() => {
    // Covers the ordering "tax number and birth date were filled while a
    // phone number was present, then the phone was cleared".
    if (!phoneProvided && taxPathComplete && taxCheckStatus === 'idle') {
      runTaxCheck();
    }
  }, [phoneProvided, taxPathComplete]);

  const createOrUpdateUserArgs = () => ({
    userAttributes: {
      firstName,
      lastName,
      // Omit the phone entirely when the field is empty (or holds only
      // the dial code) — the backend then requires a Vero-valid tax
      // number instead.
      ...(phoneProvided && {
        phoneNumber:
          phoneNumber.charAt(0) === '+' ? phoneNumber : '+' + phoneNumber,
      }),
      ...(dateOfBirthString && { dateOfBirth: dateOfBirthString }),
      ...(trimmedTaxNumber && { taxNumber: trimmedTaxNumber }),
    },
    projectKeyOfProjectToJoin: projectKey,
    businessIdOfEmployerToJoinWith: businessId,
    projectId,
    createRecordForUser: dialogMeta?.createRecordForUser,
    recordDate: shouldShowRecordDate
      ? moment(recordDate).toISOString()
      : undefined,
    ...(allowJoinByIdentity && { allowJoinByIdentity: true }),
  });

  const onConfirm = async () => {
    if (submitting) {
      return;
    }
    setSuggestedUser(null);
    setEmployerError(false);
    setSubmitting(allowJoinByIdentity);
    const result = await dispatch(createOrUpdateUser(createOrUpdateUserArgs()));
    setSubmitting(false);

    if (createOrUpdateUser.rejected.match(result)) {
      // ctx.throw errors arrive as plain string payloads; the 409 duplicate
      // arrives as an object carrying a suggestedUser.
      if (result.payload === 'employer-not-users-employer') {
        setEmployerError(true);
      } else if (result.payload === 'invalid-tax-number') {
        setTaxCheckStatus('invalid');
        checkedTaxAndDobRef.current = '';
      } else if (result.payload === 'tax-number-validation-failed') {
        setTaxCheckStatus('error');
        checkedTaxAndDobRef.current = '';
      } else if (
        // When the phone or tax number belongs to an existing in-business
        // user, the backend returns them as a suggestion instead of creating
        // a duplicate. Offer to add that existing user to the project.
        result.payload?.errorCode === 'user-already-exists' &&
        result.payload?.suggestedUser
      ) {
        setSuggestedUser(result.payload.suggestedUser);
      }
    }
  };

  const onUseExisting = async () => {
    if (!suggestedUser) {
      return;
    }
    setJoining(true);
    setEmployerError(false);

    // A user from outside this business, found by every identity detail the
    // adder typed: the backend checks them again and joins the user, under
    // the chosen employer only if it is one of theirs.
    if (suggestedUser.identityConfirmed) {
      const result = await dispatch(
        createOrUpdateUser({ ...createOrUpdateUserArgs(), joinExistingUser: true })
      );
      setJoining(false);
      if (createOrUpdateUser.fulfilled.match(result)) {
        close();
      } else if (result.payload === 'employer-not-users-employer') {
        setEmployerError(true);
      } else {
        setSuggestedUser(null);
      }
      return;
    }

    const result = await dispatch(
      joinExistingEmployeeToProject({
        projectId,
        userId: suggestedUser.id,
        employerBusinessId:
          suggestedUser.employerBusinessId || businessId || undefined,
        recordDate: shouldShowRecordDate
          ? moment(recordDate).toISOString()
          : undefined,
        selectedUserData: suggestedUser,
      })
    );
    setJoining(false);
    if (joinExistingEmployeeToProject.fulfilled.match(result)) {
      close();
    }
  };

  const close = () => {
    dispatch(closeAddNewUserDialog());
  };

  return (
    <Dialog
      open={true}
      onClose={() => close()}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>{t('add-user-dialog.add-new-user')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => {
                setFirstName(event.target.value);
                clearIdentitySuggestion();
              }}
              value={firstName}
              label={t('add-user-dialog.first-name')}
              type="text"
              id="firstNameInput"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => {
                setLastName(event.target.value);
                clearIdentitySuggestion();
              }}
              value={lastName}
              label={t('add-user-dialog.last-name')}
              type="text"
              id="lastNameInput"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              {t('add-user-dialog.phone-or-tax-required-hint')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextInput
              onChange={(value, countryData) => {
                setPhoneNumber(value);
                setDialCode(countryData?.dialCode || '358');
                setSuggestedUser(null);
              }}
              label={t('add-user-dialog.phone-number')}
              value={phoneNumber}
              type="phone"
              id="phoneNumberInput"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <DateTimePicker
              type="date"
              onChange={(value) => {
                setDateOfBirth(value);
                clearIdentitySuggestion();
                resetTaxCheck();
              }}
              onBlur={() => runTaxCheck()}
              value={dateOfBirth}
              viewMode="days"
              timeFormat={false}
              inputProps={{
                label: t('add-user-dialog.date-of-birth'),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              onChange={(event) => {
                setTaxNumber(event.target.value);
                setSuggestedUser(null);
                resetTaxCheck();
              }}
              onBlur={() => {
                setTaxNumberBlurred(true);
                runTaxCheck();
              }}
              value={taxNumber}
              label={t('add-user-dialog.tax-number')}
              type="text"
              id="taxNumberInput"
              fullWidth
              inputProps={{ maxLength: 12 }}
              error={
                taxCheckStatus === 'invalid' ||
                taxCheckStatus === 'error' ||
                taxNumberFormatInvalid
              }
              helperText={taxNumberHelperText}
              endAdornment={
                taxCheckStatus === 'checking' ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : taxCheckStatus === 'valid' ? (
                  <InputAdornment position="end">
                    <CheckIcon />
                  </InputAdornment>
                ) : undefined
              }
            />
          </Grid>
          {shouldShowRecordDate ? (
            <Grid item xs={12}>
              <DateTimePicker
                type="date"
                onChange={(value) => setRecordDate(value)}
                value={recordDate}
                viewMode="days"
                timeFormat={false}
                inputProps={{
                  label: t('calendar.started_working_at'),
                }}
              />
            </Grid>
          ) : null}
          <h2>{t('add-user-dialog.project-to-add-user-into')}</h2>
          <Grid item xs={12}>
            <TextInput
              value={projectKey}
              onChange={(event) => setProjectKey(event.target.value)}
              label={t('add-user-dialog.project-key-of-project-to-join')}
              type="number"
              id="projectKeyInput"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              value={businessId}
              onChange={(event) => {
                setBusinessId(event.target.value);
                setEmployerError(false);
                clearIdentitySuggestion();
              }}
              label={t('add-user-dialog.business-id-of-employer-to-join-with')}
              type="text"
              id="projectKeyInput"
              fullWidth
              error={employerError}
              helperText={
                employerError
                  ? t('add-user-dialog.errors.employer-not-users-employer')
                  : undefined
              }
            />
          </Grid>
          {suggestedUser ? (
            <Grid item xs={12}>
              <div
                style={{
                  border: '1px solid rgba(0,0,0,0.2)',
                  borderRadius: 4,
                  padding: 8,
                  marginTop: 4,
                }}
              >
                <p style={{ margin: '0 0 8px' }}>
                  {suggestedUser.alreadyInProject
                    ? t('add-user-dialog.user-already-in-project')
                    : t('add-user-dialog.user-already-exists')}{' '}
                  <strong>
                    {[suggestedUser.firstName, suggestedUser.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </strong>
                  {suggestedUser.taxNumber
                    ? ` (${suggestedUser.taxNumber})`
                    : ''}
                </p>
                {suggestedUser.newEmployer ? (
                  <p style={{ margin: '0 0 8px' }}>
                    {t('add-user-dialog.adds-new-employer', {
                      name:
                        suggestedUser.newEmployer.name ||
                        suggestedUser.newEmployer.businessId,
                      businessId: suggestedUser.newEmployer.businessId,
                    })}
                  </p>
                ) : null}
                <Button
                  color="primary"
                  variant="contained"
                  disabled={joining}
                  onClick={() => onUseExisting()}
                >
                  {joining && allowJoinByIdentity ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                      className={classes.buttonProgress}
                    />
                  ) : null}
                  {suggestedUser.alreadyInProject
                    ? t('add-user-dialog.continue-to-orientation')
                    : t('add-user-dialog.use-existing-user')}
                </Button>
              </div>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>{t('common.cancel')}</Button>
        <Button
          disabled={
            !firstName ||
            !lastName ||
            !projectKey ||
            !(phoneProvided || taxPathReady) ||
            submitting ||
            (joining && allowJoinByIdentity)
          }
          type="submit"
          color="primary"
          variant="contained"
          onClick={() => onConfirm()}
        >
          {submitting ? (
            <CircularProgress
              size={16}
              color="inherit"
              className={classes.buttonProgress}
            />
          ) : null}
          {t('add-user-dialog.add-user')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
